import { IImportKpiAppUsageRequest } from '@/types/request';
import moment from 'moment';

export type ParsedUsage = IImportKpiAppUsageRequest;

const NOT_SET = '(not set)';

/**
 * 取り込む CSV を読む。対応する形式は2つ。
 *
 * - GA4「ページとスクリーン」のエクスポート（日本語・英語 UI どちらも）
 *   先頭の `#` 行に `開始日: 20260915` / `Start date: 20260915` があり、表の1列目がスクリーンクラス。
 * - AppsFlyer 用の取り込み形式（Claude が AppsFlyer から作るもの）
 *   先頭の `#` 行に開始日・終了日、表のヘッダーは `イベント名,イベント数,ユニークユーザー`。
 *
 * 名前が空の行と `(not set)` の行は読み飛ばす。`(not set)` は画面に紐づかないイベント
 * （起動直後・バックグラウンドなど）の集計で、表示回数が 0 なのにユーザー数が最大になり一覧を誤らせる。
 */
export const parseUsageCsv = (text: string): ParsedUsage => {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/);

  const comment = lines.filter((l) => l.startsWith('#')).join('\n');
  const periodStart = dateFrom(
    comment,
    /(?:開始日|Start date)\s*[:：]\s*(\d{8})/,
  );
  const periodEnd = dateFrom(comment, /(?:終了日|End date)\s*[:：]\s*(\d{8})/);
  if (!periodStart || !periodEnd) {
    throw new Error(
      'CSV の先頭に開始日・終了日が見つかりません。GA4 からエクスポートしたファイルか確認してください。',
    );
  }

  const table = lines
    .filter((l) => l.trim() !== '' && !l.startsWith('#'))
    .map(splitCsvLine);
  const [header, ...body] = table;
  if (!header) throw new Error('CSV に表がありません。');

  const col = (...names: string[]) =>
    header.findIndex((h) => names.includes(h.trim()));

  let source: ParsedUsage['source'];
  let countCol: number;
  let usersCol: number;
  let engagementCol = -1;

  if (col('イベント名') === 0) {
    source = 'appsflyer';
    countCol = col('イベント数');
    usersCol = col('ユニークユーザー');
  } else {
    source = 'ga4';
    countCol = col('表示回数', 'Views');
    usersCol = col('アクティブ ユーザー', 'Active users');
    engagementCol = col(
      'アクティブ ユーザーあたりの平均エンゲージメント時間',
      'Average engagement time per active user',
    );
  }
  if (countCol < 0 || usersCol < 0) {
    throw new Error(
      'CSV の列が想定と違います（表示回数・アクティブユーザーの列が必要です）。',
    );
  }

  const rows: ParsedUsage['rows'] = [];
  const seen = new Set<string>();
  body.forEach((cells, i) => {
    const name = (cells[0] ?? '').trim();
    if (!name || name === NOT_SET) return;
    if (seen.has(name)) throw new Error(`「${name}」の行が重複しています。`);
    seen.add(name);

    const count = toInt(cells[countCol], i + 1, '回数');
    const users = toInt(cells[usersCol], i + 1, 'ユーザー数');
    // 滞在時間は任意。空欄は 0 ではなく値なし（Number('') は 0 になる）
    const engagementRaw =
      engagementCol >= 0 ? (cells[engagementCol] ?? '').trim() : '';
    const engagement = engagementRaw === '' ? NaN : Number(engagementRaw);
    rows.push({
      name,
      count,
      users,
      engagementSeconds: Number.isFinite(engagement) ? engagement : null,
    });
  });
  if (rows.length === 0) throw new Error('取り込める行がありません。');

  return { source, periodStart, periodEnd, rows };
};

const COHORT_PLATFORMS = ['ios', 'android'] as const;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 取り込むファイルを読む。新規ファネル（AppsFlyer のインストール週ごと）は1ファイルに複数週が入るので、
 * 週ごとに分けて返す（サーバーへは1週ずつ送る）。それ以外は1期間。
 */
export const parseUsageFile = (text: string): ParsedUsage[] => {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/);
  const header = lines.find((l) => l.trim() !== '' && !l.startsWith('#'));

  return header && splitCsvLine(header)[0].trim() === '週開始'
    ? parseCohortCsv(lines)
    : [parseUsageCsv(text)];
};

/**
 * 新規ファネルの取り込み形式（Claude が AppsFlyer から作るもの）。
 * ヘッダーは `週開始,週終了,OS,イベント名,イベント数,ユニークユーザー`、OS は ios / android。
 * 各週に OS ごとの `install` 行が要る（割合の分母）。
 */
const parseCohortCsv = (lines: string[]): ParsedUsage[] => {
  const [header, ...body] = lines
    .filter((l) => l.trim() !== '' && !l.startsWith('#'))
    .map(splitCsvLine);
  const col = (name: string) => header.findIndex((h) => h.trim() === name);
  const cols = {
    start: col('週開始'),
    end: col('週終了'),
    platform: col('OS'),
    name: col('イベント名'),
    count: col('イベント数'),
    users: col('ユニークユーザー'),
  };
  if (Object.values(cols).some((c) => c < 0)) {
    throw new Error(
      'CSV の列が想定と違います（週開始・週終了・OS・イベント名・イベント数・ユニークユーザーが必要です）。',
    );
  }

  const weeks = new Map<string, ParsedUsage>();
  const seen = new Set<string>();
  body.forEach((cells, i) => {
    const line = i + 1;
    const periodStart = (cells[cols.start] ?? '').trim();
    const periodEnd = (cells[cols.end] ?? '').trim();
    if (!DATE_ONLY.test(periodStart) || !DATE_ONLY.test(periodEnd)) {
      throw new Error(
        `${line}行目の週開始・週終了が YYYY-MM-DD ではありません。`,
      );
    }
    const platform = (cells[cols.platform] ?? '').trim().toLowerCase();
    if (!(COHORT_PLATFORMS as readonly string[]).includes(platform)) {
      throw new Error(
        `${line}行目の OS が ios / android ではありません（${platform || '空'}）。`,
      );
    }
    const name = (cells[cols.name] ?? '').trim();
    if (!name) throw new Error(`${line}行目のイベント名が空です。`);

    const key = `${periodStart}:${platform}:${name}`;
    if (seen.has(key)) {
      throw new Error(
        `${periodStart} の週の「${name}」（${platform}）が重複しています。`,
      );
    }
    seen.add(key);

    const weekKey = `${periodStart}:${periodEnd}`;
    const week = weeks.get(weekKey) ?? {
      source: 'appsflyer_cohort',
      periodStart,
      periodEnd,
      rows: [],
    };
    week.rows.push({
      platform: platform as (typeof COHORT_PLATFORMS)[number],
      name,
      count: toInt(cells[cols.count], line, 'イベント数'),
      users: toInt(cells[cols.users], line, 'ユーザー数'),
    });
    weeks.set(weekKey, week);
  });
  if (weeks.size === 0) throw new Error('取り込める行がありません。');

  const result = Array.from(weeks.values()).sort((a, b) =>
    a.periodStart.localeCompare(b.periodStart),
  );
  result.forEach((w) => {
    // 推移グラフは7日おきに週をたどるので、月曜〜日曜以外の週は受け付けない（グラフから消える）
    const start = moment(w.periodStart, 'YYYY-MM-DD', true);
    const end = moment(w.periodEnd, 'YYYY-MM-DD', true);
    if (
      !start.isValid() ||
      !end.isValid() ||
      start.isoWeekday() !== 1 ||
      end.diff(start, 'days') !== 6
    ) {
      throw new Error(
        `${w.periodStart}〜${w.periodEnd} は月曜〜日曜の1週間ではありません。`,
      );
    }
    const platforms = new Set(w.rows.map((r) => r.platform));
    platforms.forEach((p) => {
      if (!w.rows.some((r) => r.platform === p && r.name === 'install')) {
        throw new Error(
          `${w.periodStart} の週に ${p} のインストール数（install）の行がありません。`,
        );
      }
    });
  });

  return result;
};

const dateFrom = (comment: string, pattern: RegExp): string | null => {
  const m = comment.match(pattern);

  return m
    ? `${m[1].slice(0, 4)}-${m[1].slice(4, 6)}-${m[1].slice(6, 8)}`
    : null;
};

const toInt = (
  value: string | undefined,
  line: number,
  label: string,
): number => {
  const raw = (value ?? '').replace(/,/g, '').trim();
  // Number('') は 0 になるので、空欄を 0 として取り込まないよう先に弾く
  const n = raw === '' ? NaN : Number(raw);
  if (!Number.isInteger(n) || n < 0) {
    throw new Error(
      `${line}行目の${label}が数値ではありません（${value ?? '空'}）。`,
    );
  }

  return n;
};

/** ダブルクォートで囲んだ値（中のカンマ・"" を含む）にも対応した1行の分割 */
const splitCsvLine = (line: string): string[] => {
  const cells: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (quoted) {
      if (c === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (c === '"') {
        quoted = false;
      } else {
        cur += c;
      }
    } else if (c === '"') {
      quoted = true;
    } else if (c === ',') {
      cells.push(cur);
      cur = '';
    } else {
      cur += c;
    }
  }
  cells.push(cur);

  return cells;
};
